'use client'

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'

import {
  useQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query'
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Info,
  MapPin,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'

import Listbox from '@/components/common/Listbox'
import TextOption from '@/components/common/Listbox/TextOption'
import PopoverV2 from '@/components/common/PopoverV2'
import { Skeleton } from '@/components/common/Skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import {
  useRecipientAnalyticsQueries,
  useRecipientSectionQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { RecipientLevel } from '@/lib/usaspending/recipientId'

import type { RecipientCertifications } from '@/types/usaspending'

import { QueryErrorBoundary } from '../DataErrorBoundary'
import { Currency, DateDisplay, NumberDisplay } from '../Formatters'
import {
  getAvailableFiscalYears,
  useDetailFilter,
} from '../shared/DetailFilterContext'
import { useRecipientDetail } from './context'
import { RecipientHierarchy } from './RecipientHierarchy'

/**
 * Certification badge configuration for the header
 */
interface CertBadgeConfig {
  key: keyof RecipientCertifications
  label: string
  shortLabel: string
  className: string
}

const CERT_BADGE_CONFIG: CertBadgeConfig[] = [
  {
    key: 'is8a',
    label: '8(a) Program',
    shortLabel: '8(a)',
    className:
      'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  },
  {
    key: 'hubzone',
    label: 'HUBZone',
    shortLabel: 'HUBZone',
    className:
      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  },
  {
    key: 'sdvosb',
    label: 'Service-Disabled Veteran',
    shortLabel: 'SDVOSB',
    className:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  {
    key: 'womanOwned',
    label: 'Woman-Owned',
    shortLabel: 'WOSB',
    className:
      'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  },
  {
    key: 'veteranOwned',
    label: 'Veteran-Owned',
    shortLabel: 'VOSB',
    className:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  {
    key: 'smallBusiness',
    label: 'Small Business',
    shortLabel: 'SB',
    className:
      'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  },
  {
    key: 'sdb',
    label: 'Small Disadvantaged',
    shortLabel: 'SDB',
    className:
      'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  },
  {
    key: 'minorityOwned',
    label: 'Minority-Owned',
    shortLabel: 'MBE',
    className:
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  },
  {
    key: 'alaskaNative',
    label: 'Alaska Native Corporation',
    shortLabel: 'ANC',
    className:
      'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300',
  },
  {
    key: 'nativeAmerican',
    label: 'Native American Owned',
    shortLabel: 'NAO',
    className:
      'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  },
  {
    key: 'nonprofit',
    label: 'Nonprofit',
    shortLabel: 'NPO',
    className:
      'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  },
  {
    key: 'educational',
    label: 'Educational',
    shortLabel: 'EDU',
    className:
      'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
  },
]

/**
 * Get active certifications from recipient certifications object
 */
function getActiveCertifications(
  certifications: RecipientCertifications | undefined,
): CertBadgeConfig[] {
  if (!certifications) return []
  return CERT_BADGE_CONFIG.filter((config) => certifications[config.key])
}

/**
 * CopyableIdentifier - Click to copy identifier values
 */
function CopyableIdentifier({
  label,
  value,
}: {
  label: string
  value: string
}) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setCopied(true)
    timeoutRef.current = setTimeout(() => {
      setCopied(false)
    }, 2000)
  }, [value])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group flex items-center gap-2 rounded px-1 -mx-1 transition-colors hover:bg-primary-100 dark:hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-1"
      title={`Copy ${label}`}
    >
      <span className="w-10 text-xs font-medium uppercase tracking-wider text-primary-400 dark:text-primary-500">
        {label}
      </span>
      <span className="rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 font-mono text-[11px] text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300">
        {value}
      </span>
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 text-primary-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-500" />
      )}
    </button>
  )
}

/**
 * Certification badges - loads from profile endpoint (may be slow)
 */
function CertificationBadges({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const [{ data: recipient }] = useSuspenseQueries({
    queries: [queries.profile(id)],
  })

  const activeCerts = useMemo(
    () => getActiveCertifications(recipient.certifications),
    [recipient.certifications],
  )

  if (activeCerts.length === 0) return null

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {activeCerts.map((cert) => (
        <span
          key={cert.key}
          title={cert.label}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold tracking-wide ${cert.className}`}
        >
          {cert.shortLabel}
        </span>
      ))}
    </div>
  )
}

function CertificationBadgesSkeleton() {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <Skeleton className="h-5 w-12" />
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-5 w-14" />
    </div>
  )
}

/**
 * Level configuration for display
 */
interface LevelConfig {
  label: string
  description: string
  className: string
}

const LEVEL_STYLES: Record<RecipientLevel, string> = {
  P: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  C: 'border-purple-300 bg-purple-50 text-purple-700 dark:border-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  R: 'border-primary-300 bg-primary-50 text-primary-600 dark:border-primary-600 dark:bg-primary-800 dark:text-primary-400',
}

function getLevelConfig(
  level: RecipientLevel,
  selfReferential: boolean,
): LevelConfig {
  const className = LEVEL_STYLES[level]
  switch (level) {
    case 'P':
      return selfReferential
        ? {
            label: 'Ultimate Parent View',
            description:
              'Broader view — may include this entity\u2019s own awards plus awards reported for related parts of the business.',
            className,
          }
        : {
            label: 'Parent View',
            description:
              'Awards for this entity and all its subsidiaries combined',
            className,
          }
    case 'C':
      return selfReferential
        ? {
            label: 'Child View',
            description:
              'Narrower view — only awards where this legal entity is the named recipient.',
            className,
          }
        : {
            label: 'Child View',
            description:
              'Awards for this entity as a subsidiary within a parent group',
            className,
          }
    default:
      return {
        label: 'Recipient View',
        description: 'Awards attributed directly to this entity only',
        className,
      }
  }
}

/**
 * Level indicator with switcher — shows the current recipient level and allows
 * switching between available levels (P/C/R) via a popover.
 *
 * Uses available levels from the basicInfo endpoint (backed by rpt.recipient_profile
 * in PostgreSQL — the canonical source), not Turbopuffer which may be incomplete.
 */
function RecipientLevelIndicator({ id }: { id: string }) {
  const { recipientHash, recipientLevel } = useRecipientDetail()
  const router = useRouter()
  const queries = useRecipientSectionQueries()
  const [isNavigating, setIsNavigating] = useState(false)

  // Pages Router: track navigation state via router.events
  useEffect(() => {
    const onStart = () => setIsNavigating(true)
    const onDone = () => setIsNavigating(false)
    router.events.on('routeChangeStart', onStart)
    router.events.on('routeChangeComplete', onDone)
    router.events.on('routeChangeError', onDone)
    return () => {
      router.events.off('routeChangeStart', onStart)
      router.events.off('routeChangeComplete', onDone)
      router.events.off('routeChangeError', onDone)
    }
  }, [router.events])

  const { data: recipient } = useSuspenseQuery(queries.basicInfo(id))

  const isSelfReferential = recipient.selfReferential ?? false
  const currentConfig = getLevelConfig(recipientLevel, isSelfReferential)
  const availableLevels = recipient.availableLevels ?? [recipientLevel]
  const hasMultipleLevels = availableLevels.length > 1

  const handleSwitchLevel = useCallback(
    (level: RecipientLevel) => {
      if (level === recipientLevel || isNavigating) return
      void router.push(`/recipients/usaspending/${recipientHash}-${level}`)
    },
    [router, recipientHash, recipientLevel, isNavigating],
  )

  if (!hasMultipleLevels) {
    return (
      <PopoverV2>
        <PopoverV2.Trigger asChild>
          <button
            type="button"
            className={twMerge(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors hover:opacity-80',
              currentConfig.className,
            )}
          >
            {currentConfig.label}
            <Info className="h-2.5 w-2.5 opacity-60" />
          </button>
        </PopoverV2.Trigger>
        <PopoverV2.Content align="start" className="w-80 p-3">
          <p className="text-sm font-medium text-primary-700 dark:text-primary-200">
            {currentConfig.label}
          </p>
          <p className="mt-1 text-xs text-primary-500 dark:text-primary-400">
            {currentConfig.description}
          </p>
          <p className="mt-2 text-xs text-primary-400 dark:text-primary-500">
            This is the only data scope available for this entity.
          </p>
        </PopoverV2.Content>
      </PopoverV2>
    )
  }

  return (
    <div className="inline-flex items-center gap-2">
      <PopoverV2>
        <PopoverV2.Trigger asChild>
          <button
            type="button"
            className={twMerge(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors hover:opacity-80',
              currentConfig.className,
            )}
          >
            {currentConfig.label}
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
        </PopoverV2.Trigger>
        <PopoverV2.Content align="start" className="w-96 p-0">
          <div className="border-b border-primary-100 px-3 py-2 dark:border-primary-800">
            <p className="text-xs font-semibold text-primary-700 dark:text-primary-300">
              Data Scope
            </p>
            <p className="text-xs text-primary-400 dark:text-primary-500">
              Switch how this entity&apos;s awards are aggregated
            </p>
          </div>
          <div className="py-1">
            {(['C', 'R', 'P'] as const).map((level) => {
              if (!availableLevels.includes(level)) return null
              const config = getLevelConfig(level, isSelfReferential)
              const isActive = level === recipientLevel
              return (
                <button
                  key={level}
                  type="button"
                  disabled={isNavigating}
                  onClick={() => handleSwitchLevel(level)}
                  className={twMerge(
                    'flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors',
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-800/50'
                      : 'hover:bg-primary-50 dark:hover:bg-primary-800/30',
                    isNavigating && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="mt-0.5 w-[9.5rem] shrink-0">
                    <span
                      className={twMerge(
                        'inline-flex items-center rounded-full border px-1.5 py-0.5 text-xs font-medium',
                        config.className,
                      )}
                    >
                      {config.label}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-primary-500 dark:text-primary-400">
                      {config.description}
                    </p>
                  </div>
                  {isActive && (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-500" />
                  )}
                </button>
              )
            })}
          </div>
        </PopoverV2.Content>
      </PopoverV2>
      {isSelfReferential && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-primary-400 transition-colors hover:text-primary-600 dark:text-primary-500 dark:hover:text-primary-300"
            >
              <Info className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            className="max-w-80 text-xs leading-relaxed"
          >
            {recipientLevel === 'P' ? (
              <>
                <p>
                  Some federal award records use this same company as both the
                  award recipient and the related-company reference.
                </p>
                <p className="mt-2">
                  This page is showing the broader view associated with this
                  company. That total may include this entity&apos;s own awards
                  plus awards reported for related parts of the business, so it
                  may not match the direct-award total for the same company.
                </p>
              </>
            ) : (
              <>
                <p>
                  Some federal award records use this same company in both the
                  award recipient field and the related-company field.
                </p>
                <p className="mt-2">
                  This page is showing the narrower view where this legal entity
                  is the named recipient. A broader view of the same company may
                  also reflect affiliated or divisional activity when the source
                  record links it, so the totals between the two views may
                  differ.
                </p>
              </>
            )}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

/**
 * CAGE code identifier - loads from profile endpoint (progressive loading)
 */
function CageCodeIdentifier({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const [{ data: recipient }] = useSuspenseQueries({
    queries: [queries.profile(id)],
  })

  if (!recipient.cageCode) return null

  return <CopyableIdentifier label="CAGE" value={recipient.cageCode} />
}

/**
 * KPI Strip - loads from stats endpoint
 */
function KPIStrip({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)
  const stats = dashboard.stats
  if (!stats) return null

  return (
    <div className="flex flex-wrap items-center gap-4 divide-x divide-primary-200 dark:divide-primary-700">
      <div className="flex flex-col">
        <span className="text-xs font-medium uppercase tracking-wider text-primary-500 sm:text-xs dark:text-primary-400">
          Total Obligated
        </span>
        <span className="text-sm font-bold tabular-nums text-primary-900 sm:text-base dark:text-primary-100">
          <Currency value={stats.totalObligatedAmount} compact />
        </span>
      </div>
      <div className="flex flex-col pl-4">
        <span className="text-xs font-medium uppercase tracking-wider text-primary-500 sm:text-xs dark:text-primary-400">
          Prime Awards
        </span>
        <span className="text-sm font-bold tabular-nums text-primary-900 sm:text-base dark:text-primary-100">
          <NumberDisplay value={stats.totalPrimeAwards} />
        </span>
      </div>
      <div className="flex flex-col pl-4">
        <span className="text-xs font-medium uppercase tracking-wider text-primary-500 sm:text-xs dark:text-primary-400">
          Last Transaction
        </span>
        <span className="text-sm font-bold tabular-nums text-primary-900 sm:text-base dark:text-primary-100">
          <DateDisplay dateStr={stats.lastTransactionDate} />
        </span>
      </div>
    </div>
  )
}

function KPIStripSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-4 divide-x divide-primary-200 dark:divide-primary-700">
      <div className="flex flex-col">
        <Skeleton className="mb-1 h-3 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="flex flex-col pl-4">
        <Skeleton className="mb-1 h-3 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="flex flex-col pl-4">
        <Skeleton className="mb-1 h-3 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
  )
}

/**
 * RecipientDetail.Header
 *
 * Displays the recipient name, certifications, and identifiers (UEI, DUNS, CAGE).
 * Uses progressive loading: basic info loads immediately, certifications and stats load progressively.
 */
export function RecipientHeader() {
  const { recipientId } = useRecipientDetail()

  return (
    <QueryErrorBoundary title="Failed to load recipient" rethrowNotFound>
      <Suspense fallback={<RecipientHeaderSkeleton />}>
        <RecipientHeaderContent id={recipientId} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function RecipientHeaderContent({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const analyticsQueries = useRecipientAnalyticsQueries()
  // Use basicInfo for immediate display (fast, RDS-only)
  const [{ data: recipient }] = useSuspenseQueries({
    queries: [queries.basicInfo(id)],
  })
  const { location } = recipient
  const { state, actions } = useDetailFilter()
  const [isPending, startTransition] = useTransition()
  const referenceNowMs = useRenderNowMs({ refreshMs: 60_000 })

  // Derive active fiscal years from the all-time dashboard time series
  // (same cache key as SpendingTrendSection — no extra request)
  const { data: allTimeDashboard } = useQuery(analyticsQueries.dashboard(id))
  const fiscalYears = useMemo(() => {
    const ts = allTimeDashboard?.timeSeries
    if (!ts?.length)
      return getAvailableFiscalYears(referenceNowMs ?? Date.now())
    const fySet = new Set(ts.map((d) => (d.month >= 10 ? d.year + 1 : d.year)))
    return Array.from(fySet).sort((a, b) => a - b)
  }, [allTimeDashboard?.timeSeries, referenceNowMs])

  // UEI/DUNS from basicInfo (fast), CAGE loaded separately from profile
  const identifiers = useMemo(
    () =>
      [
        { label: 'UEI', value: recipient.uei },
        { label: 'DUNS', value: recipient.duns },
      ].filter((item) => item.value),
    [recipient.uei, recipient.duns],
  )

  return (
    <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
      <div className="mx-auto max-w-7xl px-4 py-3 md:px-8">
        {/* Breadcrumbs */}
        <nav className="mb-2 flex items-center gap-1.5 text-xs font-medium text-primary-400 dark:text-primary-500">
          <Link
            href="/recipients/search"
            className="transition-colors hover:text-accent-500"
          >
            Recipients
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="truncate text-primary-600 dark:text-primary-400">
            {recipient.name}
          </span>
        </nav>

        {/* Main header row - icon, name/badges on left, identifiers on right */}
        <div className="flex items-start justify-between gap-4">
          {/* Left: Icon and Name with Certifications */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {/* Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
              <Building2 className="h-5 w-5 text-primary-400 dark:text-primary-500" />
            </div>

            {/* Name, Location, and Certifications */}
            <div className="flex-1 min-w-0">
              {/* Name row with location */}
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-lg font-bold leading-tight tracking-tight text-primary-900 sm:text-xl md:text-2xl dark:text-primary-50">
                  {recipient.name}
                </h1>

                {/* Location inline */}
                {location && (
                  <span className="flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400">
                    <MapPin className="h-3 w-3" />
                    {[location.city, location.state].filter(Boolean).join(', ')}
                  </span>
                )}

                {/* Recipient level indicator with scope switcher */}
                <QueryErrorBoundary
                  title="level"
                  serviceUnavailableFallback={null}
                >
                  <Suspense fallback={null}>
                    <RecipientLevelIndicator id={id} />
                  </Suspense>
                </QueryErrorBoundary>
              </div>

              {/* Certification badges - progressive loading */}
              <QueryErrorBoundary
                title="certifications"
                serviceUnavailableFallback={null}
              >
                <Suspense fallback={<CertificationBadgesSkeleton />}>
                  <CertificationBadges id={id} />
                </Suspense>
              </QueryErrorBoundary>
            </div>
          </div>

          {/* Right: Identifiers - vertical layout */}
          <div className="hidden sm:flex shrink-0 flex-col gap-0.5">
            {identifiers.map((identifier) => (
              <CopyableIdentifier
                key={identifier.label}
                label={identifier.label}
                value={identifier.value!}
              />
            ))}
            {/* CAGE code - progressive loading from profile */}
            <QueryErrorBoundary title="cage" serviceUnavailableFallback={null}>
              <Suspense fallback={null}>
                <CageCodeIdentifier id={id} />
              </Suspense>
            </QueryErrorBoundary>
          </div>
        </div>

        {/* Mobile-only identifiers row */}
        <div className="mt-2 flex sm:hidden flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {identifiers.map((id) => (
            <CopyableIdentifier
              key={id.label}
              label={id.label}
              value={id.value!}
            />
          ))}
          {/* CAGE code - progressive loading from profile */}
          <QueryErrorBoundary title="cage" serviceUnavailableFallback={null}>
            <Suspense fallback={null}>
              <CageCodeIdentifier id={id} />
            </Suspense>
          </QueryErrorBoundary>
        </div>

        {/* KPI Strip integrated into header */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-primary-100 pt-3 dark:border-primary-800">
          {/* KPI Cards - progressive loading */}
          <QueryErrorBoundary
            title="stats"
            serviceUnavailableFallback={
              <p className="text-xs text-primary-400 dark:text-primary-500">
                Stats temporarily unavailable
              </p>
            }
          >
            <Suspense fallback={<KPIStripSkeleton />}>
              <div className={isPending ? 'opacity-50 transition-opacity' : ''}>
                <KPIStrip id={id} />
              </div>
            </Suspense>
          </QueryErrorBoundary>

          {/* Analytics filters */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-primary-500 dark:text-primary-400">
              FY:
            </span>
            <Listbox
              value={state.fiscalYear}
              onChange={(value: 'all' | number) =>
                startTransition(() => actions.setFiscalYear(value))
              }
            >
              <div className="relative">
                <Listbox.Button
                  className={twMerge(
                    'flex items-center gap-1 rounded-md border border-primary-200 bg-white px-2 py-0.5 text-xs font-medium',
                    'text-primary-900 dark:border-primary-600 dark:bg-primary-800 dark:text-primary-100',
                    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                    'cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500',
                    'ring-0',
                    state.fiscalYear !== 'all' &&
                      'border-accent-300 bg-accent-50 text-accent-900 dark:border-accent-700/80 dark:bg-accent-950/30 dark:text-accent-100',
                  )}
                >
                  <span>
                    {state.fiscalYear === 'all'
                      ? 'All Years'
                      : `FY${state.fiscalYear}`}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-primary-400" />
                </Listbox.Button>
                <Listbox.Options className="mt-1 min-w-[100px]">
                  <Listbox.Option value="all">
                    <TextOption
                      text="All Years"
                      selected={state.fiscalYear === 'all'}
                    />
                  </Listbox.Option>
                  {[...fiscalYears].reverse().map((fy) => (
                    <Listbox.Option key={fy} value={fy}>
                      <TextOption
                        text={`FY${fy}`}
                        selected={state.fiscalYear === fy}
                      />
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        </div>

        {/* Org Hierarchy Strip — parent/children/siblings from Turbopuffer */}
        <RecipientHierarchy />
      </div>
    </header>
  )
}

function RecipientHeaderSkeleton() {
  return (
    <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
      <div className="mx-auto max-w-7xl px-4 py-3 md:px-8">
        {/* Breadcrumbs skeleton */}
        <Skeleton className="mb-2 h-3 w-40" />

        {/* Main row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
              <Building2 className="h-5 w-5 text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <Skeleton className="h-6 w-64 mb-1.5" />
              <div className="flex gap-1.5">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-14" />
              </div>
            </div>
          </div>
          {/* Vertical identifiers skeleton */}
          <div className="hidden sm:flex flex-col gap-0.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
        </div>

        {/* KPI Strip skeleton */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-primary-100 pt-3 dark:border-primary-800">
          <div className="flex flex-wrap items-center gap-4 divide-x divide-primary-200 dark:divide-primary-700">
            <div className="flex flex-col">
              <Skeleton className="mb-1 h-3 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="flex flex-col pl-4">
              <Skeleton className="mb-1 h-3 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="flex flex-col pl-4">
              <Skeleton className="mb-1 h-3 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
          <Skeleton className="h-8 w-28" />
        </div>
      </div>
    </header>
  )
}
